import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  DocumentArrowUpIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';

const ValidSerials = () => {
  const [validSerials, setValidSerials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', 'used', 'available'
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [newSerial, setNewSerial] = useState('');
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedBulkProduct, setSelectedBulkProduct] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);

  const fetchValidSerials = useCallback(async (page = 1, search = '', status = '') => {
    try {
      setLoading(true);
      console.log('🔍 Fetching valid serials with params:', { page, search, status });

      const response = await adminService.getValidSerials(page, 50, search, status);
      console.log('✅ Valid serials response:', response);

      if (response && response.success && response.data) {
        setValidSerials(response.data.serials || []);
        setPagination(response.data.pagination || {});
        setStats(response.data.stats || {});
      } else {
        console.error('❌ Invalid response format:', response);
        setValidSerials([]);
        setPagination({});
        setStats({});
        toast.error('Invalid response format from server');
      }
    } catch (error) {
      console.error('❌ Fetch valid serials error:', error);
      toast.error(error.message || 'Failed to fetch valid serial numbers');
      setValidSerials([]);
      setPagination({});
      setStats({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchValidSerials(currentPage, searchTerm, statusFilter);
    fetchProducts();
  }, [currentPage, searchTerm, statusFilter, fetchValidSerials]);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await adminService.getProducts();
      if (response.success) {
        setProducts(response.data.filter(product => product.isActive));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
      fetchValidSerials(1, searchTerm, statusFilter);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchValidSerials, statusFilter]);

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleUpload = async () => {
    if (!csvData.trim()) {
      toast.error('Please enter CSV data');
      return;
    }

    if (!selectedBulkProduct) {
      toast.error('Please select a product for bulk upload');
      return;
    }

    try {
      setUploading(true);
      const response = await adminService.uploadValidSerials(csvData, selectedBulkProduct);
      toast.success(response.message);
      setShowUploadModal(false);
      setCsvData('');
      setSelectedBulkProduct('');
      // Refresh data with current filters
      fetchValidSerials(currentPage, searchTerm, statusFilter);
    } catch (error) {
      toast.error(error.message || 'Failed to upload serial numbers');
    } finally {
      setUploading(false);
    }
  };

  const handleAddSerial = async () => {
    if (!newSerial.trim()) {
      toast.error('Please enter a serial number');
      return;
    }

    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    try {
      setAdding(true);
      const response = await adminService.addValidSerial(newSerial, '', selectedProduct);
      toast.success(response.message);
      setShowAddModal(false);
      setNewSerial('');
      setSelectedProduct('');
      // Refresh data with current filters
      fetchValidSerials(currentPage, searchTerm, statusFilter);
    } catch (error) {
      toast.error(error.message || 'Failed to add serial number');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveSerial = async (serialNumber) => {
    if (!window.confirm(`Are you sure you want to remove serial number ${serialNumber}?`)) {
      return;
    }

    try {
      const response = await adminService.removeValidSerial(serialNumber);
      toast.success(response.message);
      // Refresh data with current filters
      fetchValidSerials(currentPage, searchTerm, statusFilter);
    } catch (error) {
      toast.error(error.message || 'Failed to remove serial number');
    }
  };

  return (
    <Layout title="Valid Serial Numbers">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Valid Serial Numbers
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Manage the list of valid serial numbers that installers can register
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary text-sm px-3 py-2 w-full sm:w-auto touch-manipulation"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Add Serial</span>
              <span className="sm:hidden">Add</span>
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn-secondary text-sm px-3 py-2 w-full sm:w-auto touch-manipulation"
            >
              <DocumentArrowUpIcon className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Upload CSV</span>
              <span className="sm:hidden">Upload</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Valid Serials
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.total || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <div className="h-4 w-4 bg-green-600 rounded-full"></div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Available
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.available || 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                <div className="h-4 w-4 bg-red-600 rounded-full"></div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Used
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.used || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search serial numbers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <div className="relative">
                <FunnelIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="form-input pl-10 appearance-none"
                >
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="used">Used</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Serial Numbers List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Serial Numbers ({pagination.total || 0})
              </h3>
              {pagination.total > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : validSerials.length === 0 ? (
              <div className="text-center py-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm || statusFilter ? 'No serial numbers match your filters' : 'No valid serial numbers found'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {validSerials.map((serial, index) => (
                  <div
                    key={serial.id || index}
                    className="flex flex-col p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                        {serial.serialNumber}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          serial.isUsed
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {serial.isUsed ? 'Used' : 'Available'}
                        </span>
                        <button
                          onClick={() => handleRemoveSerial(serial.serialNumber)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Remove serial number"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {serial.product && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {serial.product.name} - {serial.product.model}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {serial.product.type} • {serial.product.points} points
                        </p>
                      </div>
                    )}
                    {serial.notes && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {serial.notes}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Added: {new Date(serial.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeftIcon className="h-4 w-4 mr-1" />
                        Previous
                      </button>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                          let pageNum;
                          if (pagination.pages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= pagination.pages - 2) {
                            pageNum = pagination.pages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-2 text-sm font-medium rounded-md ${
                                currentPage === pageNum
                                  ? 'bg-orange-600 text-white'
                                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= pagination.pages}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    </div>

                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Page {currentPage} of {pagination.pages}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Upload Serial Numbers (CSV)
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Product *</label>
                  <select
                    value={selectedBulkProduct}
                    onChange={(e) => setSelectedBulkProduct(e.target.value)}
                    className="form-input"
                    disabled={loadingProducts}
                  >
                    <option value="">Select a product for all serials...</option>
                    {products.map((product) => (
                      <option key={product._id} value={product._id}>
                        {product.name} - {product.model} ({product.type}) - {product.points} pts
                      </option>
                    ))}
                  </select>
                  {loadingProducts && (
                    <p className="text-sm text-gray-500 mt-1">Loading products...</p>
                  )}
                </div>
                <div>
                  <label className="form-label">
                    CSV Data (one serial number per line)
                  </label>
                  <textarea
                    value={csvData}
                    onChange={(e) => setCsvData(e.target.value)}
                    className="form-input"
                    rows="10"
                    placeholder="SUNX001001&#10;SUNX001002&#10;SUNX001003&#10;..."
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Format: One serial number per line. Headers will be ignored. All serials will be assigned to the selected product.
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setCsvData('');
                    setSelectedBulkProduct('');
                  }}
                  disabled={uploading}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || !selectedBulkProduct}
                  className="btn-primary"
                >
                  {uploading ? (
                    <>
                      <LoadingSpinner size="sm" color="white" />
                      <span className="ml-2">Uploading...</span>
                    </>
                  ) : (
                    'Upload'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Serial Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Add Serial Number
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Serial Number</label>
                  <input
                    type="text"
                    value={newSerial}
                    onChange={(e) => setNewSerial(e.target.value.toUpperCase())}
                    className="form-input"
                    placeholder="SUNX001001"
                  />
                </div>
                <div>
                  <label className="form-label">Product *</label>
                  <select
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    className="form-input"
                    disabled={loadingProducts}
                  >
                    <option value="">Select a product...</option>
                    {products.map((product) => (
                      <option key={product._id} value={product._id}>
                        {product.name} - {product.model} ({product.type}) - {product.points} pts
                      </option>
                    ))}
                  </select>
                  {loadingProducts && (
                    <p className="text-sm text-gray-500 mt-1">Loading products...</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewSerial('');
                    setSelectedProduct('');
                  }}
                  disabled={adding}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSerial}
                  disabled={adding || !selectedProduct}
                  className="btn-primary"
                >
                  {adding ? (
                    <>
                      <LoadingSpinner size="sm" color="white" />
                      <span className="ml-2">Adding...</span>
                    </>
                  ) : (
                    'Add Serial'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ValidSerials;
